import { useState, useEffect } from 'react';

import { useDonationContextProvider } from 'context/DonationContextProvider';
import { useBranchContext } from 'context/BranchContext';

import placeholderImage from 'assets/img/we-raise-images/image-placeholder.png';

import { Button, Card, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import LoadingContainer from 'components/common/LoadingContainer';
import { PackageDrawer } from './PackageDrawer';
import { getUrgentAppealsWithPackages } from '../../lib/supabaseQueries';

function PackageList() {
  const [selectedPackage, setSelectedPackage] = useState();
  const [packageList, setPackageList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);

  const organizationId = process.env.REACT_APP_ORGANIZATION_ID;
  const { branchId, loading: branchLoading } = useBranchContext(); // Get branch from subdomain

  const { setIsPackageDrawerOpen } = useDonationContextProvider();

  // Fetch urgent appeals with packages from Supabase
  useEffect(() => {
    const fetchAppeals = async () => {
      setLoading(true);

      try {
        // Filter by branch from subdomain
        const { data, error } = await getUrgentAppealsWithPackages(organizationId, branchId);

        if (error) {
          console.error('Error fetching urgent appeals:', error);
          setPackageList([]);
        } else {
          setPackageList(data || []);
        }
      } catch (error) {
        console.error('Error fetching urgent appeals:', error);
        setPackageList([]);
      } finally {
        setLoading(false);
      }
    };

    // Wait for branch to load before fetching
    if (organizationId && !branchLoading) {
      fetchAppeals();
    }
  }, [organizationId, branchId, branchLoading]);

  // Show loading while branch is loading or data is being fetched
  if (branchLoading || loading) {
    return <LoadingContainer />;
  }

  const donateNowHandler = packageItem => {
    setSelectedPackage(packageItem);
    setIsPackageDrawerOpen(true);
  };

  return (
    <>
      <div className="">
        <h2 className="text-center">Current Appeals</h2>
        <div
          style={{
            width: '50px',
            height: '2px',
            backgroundColor: 'red'
            // margin: '0 auto 30px'
          }}
          className="mx-auto mb-4 mt-3"
        />

        <div className="row">
          {packageList.map(item => (
            <div
              key={item.id}
              className="col-12 col-sm-6 col-md-4 d-flex justify-content-center mb-4"
            >
              <Card
                className="shadow-sm rounded-3 border border-2 bg-wr-donation-grey-0 wr-donation-text-grey-900 appeal-card"
                style={{ 
                  flex: '1 1 280px', 
                  maxWidth: '320px',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                onClick={() => donateNowHandler(item)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <div className="position-relative">
                  <Image
                    src={item?.images?.primary || placeholderImage}
                    alt={item.package_title}
                    style={{
                      objectFit: 'cover',
                      height: '180px'
                    }}
                    className="w-100 rounded-top"
                  />
                </div>
                <Card.Body className="p-3 d-flex flex-column gap-2" style={{ minHeight: '140px' }}>
                  <div className="d-flex flex-column justify-content-between gap-2 flex-grow-1">
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="d-flex align-items-start gap-1 flex-grow-1" style={{ minWidth: 0 }}>
                        <span
                          className="fw-medium"
                          style={{ lineHeight: '1.4' }}
                        >
                          {item.title}
                        </span>
                        {item.description && (
                          <span
                            className="text-muted flex-shrink-0 position-relative d-inline-flex align-items-center justify-content-center"
                            style={{ cursor: 'pointer', marginTop: '2px', width: '24px', height: '24px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveTooltip(activeTooltip === item.id ? null : item.id);
                            }}
                          >
                            <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                            {activeTooltip === item.id && (
                              <div
                                className="position-absolute bg-dark text-white rounded p-2"
                                style={{
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginBottom: '8px',
                                  fontSize: '0.8rem',
                                  whiteSpace: 'pre-line',
                                  minWidth: '200px',
                                  maxWidth: '280px',
                                  zIndex: 1050,
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                }}
                              >
                                {item.description}
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    borderLeft: '6px solid transparent',
                                    borderRight: '6px solid transparent',
                                    borderTop: '6px solid #212529'
                                  }}
                                />
                              </div>
                            )}
                          </span>
                        )}
                      </div>
                      {item.package?.donation_amount && (() => {
                        const raw = item.package.donation_amount;
                        const amounts = (Array.isArray(raw) ? raw : [raw])
                          .filter(a => a !== 'custom')
                          .map(Number)
                          .filter(n => !isNaN(n));
                        if (amounts.length === 0) return null;
                        const smallest = Math.min(...amounts);
                        return (
                          <span
                            className="fw-bold text-white rounded-pill px-2 py-1 flex-shrink-0"
                            style={{
                              backgroundColor: '#22c55e',
                              fontSize: '0.85rem',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {amounts.length > 1 ? `From £${smallest.toLocaleString()}` : `£${smallest.toLocaleString()}`}
                          </span>
                        );
                      })()}
                    </div>

                    <Button
                      variant="warning"
                      className="w-100 text-dark py-3"
                      style={{ backgroundColor: '#FFD75E', border: 'none', fontSize: '1.1rem' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        donateNowHandler(item);
                      }}
                    >
                      Add to Basket
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      </div>
      {selectedPackage && selectedPackage.package && (() => {
        // Safely parse donation_amounts
        let parsedAmounts = [];
        try {
          parsedAmounts = selectedPackage.package.donation_amounts
            ? JSON.parse(selectedPackage.package.donation_amounts)
            : [];
        } catch (e) {
          console.error('Error parsing donation_amounts:', e);
        }

        // Safely parse donation_types
        let parsedTypes = [];
        try {
          parsedTypes = selectedPackage.package.donation_types
            ? JSON.parse(selectedPackage.package.donation_types)
            : [];
        } catch (e) {
          console.error('Error parsing donation_types:', e);
        }

        return (
          <PackageDrawer
            packageId={selectedPackage.package_id}
            packageName={selectedPackage.title}
            paymentTypeOptions={selectedPackage.package.payment_options || []}
            amountOptions={parsedAmounts.filter(amount => amount.amount !== 'custom')}
            hasCustomAmount={!!parsedAmounts.find(amount => amount.amount === 'custom')}
            typeOptions={parsedTypes}
            transactionFee={null}
            branchId={selectedPackage.package.branch_id}
          />
        );
      })()}
    </>
  );
}

export { PackageList };
